// Core
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, Crumbs, DataTable, KpiStrip, PageHead, Segmented } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  selectAllMembers,
  useDeleteMemberByIdMutation,
  useGetMembersByCommunityIdQuery,
  useInviteMemberMutation,
} from "state/members/membersApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";
import { formatDate } from "utils/time";

function ListMembers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["members", "community"]);

  const [statusFilter, setStatusFilter] = useState("ALL");

  // GET members
  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ members: (data && selectAllMembers(data)) || [] }),
    }
  );

  // DELETE member
  const [deleteMember] = useDeleteMemberByIdMutation();
  const onDeleteMemberClicked = async (memberId) => {
    const action = async () => {
      try {
        const response = await deleteMember({ memberId, communityId: community.id }).unwrap();
        if (response) {
          dispatch(
            openNotificationPopup({
              notification: response.message,
              type: "error",
            })
          );
        } else {
          dispatch(
            openNotificationPopup({
              notification: t("notifications.deleted_success"),
              type: "success",
            })
          );
        }
      } catch (err) {
        dispatch(
          openNotificationPopup({
            notification: err,
            type: "error",
          })
        );
      }
    };

    dispatch(
      openWarningAlert({
        confirmText: t("delete.confirm_button", { ns: "community" }),
        confirmAction: action,
      })
    );
  };

  // INVITE member
  const [inviteMember] = useInviteMemberMutation();
  const onInviteMemberClicked = async (memberId) => {
    try {
      await inviteMember({ memberId, communityId: community.id }).unwrap();
      dispatch(
        openNotificationPopup({
          notification: t("notifications.invite_sent"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        openNotificationPopup({
          notification: t("notifications.invite_error"),
          type: "error",
        })
      );
    }
  };

  const openMember = (memberId) => navigate(`/communities/${community.id}/members/${memberId}`);

  const rows = useMemo(
    () =>
      members
        .filter((m) => statusFilter === "ALL" || m.status === statusFilter)
        .map((m) => ({
          id: m.id,
          name: m.name,
          email: m.email || "",
          phone: m.phone?.length > 0 ? m.phone : "—",
          joined: m.created_at ? formatDate(m.created_at) : "—",
          joinedAt: m.created_at || "",
          verified: m.is_verified ?? false,
          host: m.is_host ?? false,
          promoter: m.is_promoter ?? false,
          status: m.status,
          access: m.has_dashboard_access ?? false,
        })),
    [members, statusFilter]
  );

  const yesNo = (v) =>
    v ? (
      <Chip tone="green">{t("table.badges.yes")}</Chip>
    ) : (
      <Chip tone="gray">{t("table.badges.no")}</Chip>
    );

  const columns = [
    {
      key: "name",
      label: t("table.columns.member"),
      render: (v, row) => (
        <div className="rec-cell-member">
          <div className="rec-av-sm" style={{ background: avColor(v) }}>
            {initials(v)}
          </div>
          <div>
            <b>{v}</b>
            <small>{row.email}</small>
          </div>
        </div>
      ),
    },
    { key: "phone", label: t("table.columns.phone") },
    { key: "joined", label: t("table.columns.joined"), sortValue: (row) => row.joinedAt },
    {
      key: "verified",
      label: t("table.columns.verified"),
      render: (v) =>
        v ? (
          <Chip tone="green">
            <Icon name="check" style={{ fontSize: 13 }} />
            {t("table.badges.verified")}
          </Chip>
        ) : (
          <Chip tone="amber">{t("table.badges.pending")}</Chip>
        ),
    },
    { key: "host", label: t("table.columns.is_host"), render: yesNo },
    { key: "promoter", label: t("table.columns.is_promoter"), render: yesNo },
    {
      key: "status",
      label: t("table.columns.status"),
      render: (v) =>
        v === "ACTIVE" ? (
          <Chip tone="green">{t("table.badges.active")}</Chip>
        ) : (
          <Chip tone="red">{t("table.badges.inactive")}</Chip>
        ),
    },
    {
      key: "access",
      label: t("table.columns.dashboard_access"),
      render: (v, row) => (
        <div className="flex items-center gap-2">
          <Chip tone={v ? "teal" : "amber"}>
            {v ? t("table.badges.has_access") : t("table.badges.no_access")}
          </Chip>
          <button
            type="button"
            className="rec-icon-btn !w-[30px] !h-[30px]"
            title={t("table.actions.send_invite")}
            onClick={(e) => {
              e.stopPropagation();
              onInviteMemberClicked(row.id);
            }}
          >
            <Icon name="mail" style={{ fontSize: 17 }} />
          </button>
        </div>
      ),
    },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <div className="rec-row-actions">
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.edit")}
            onClick={(e) => {
              e.stopPropagation();
              openMember(row.id);
            }}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.delete")}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteMemberClicked(row.id);
            }}
          >
            <Icon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: "ALL", label: t("table.filters.status.all") },
    { value: "ACTIVE", label: t("table.filters.status.active") },
    { value: "INACTIVE", label: t("table.filters.status.inactive") },
  ];

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="member-list" />
      <Crumbs items={[{ label: community?.name || "—" }, { label: t("title") }]} />
      <PageHead
        title={t("title")}
        subtitle={t("subtitle")}
        actions={
          <Button onClick={() => navigate(`/communities/${community.id}/members/new`)}>
            <Icon name="add" />
            {t("new_button")}
          </Button>
        }
      />

      <KpiStrip
        className="mb-rec"
        cols={4}
        items={[
          { icon: "groups", value: String(members.length), label: t("stats.total"), tint: "teal" },
          {
            icon: "verified",
            value: String(members.filter((m) => m.is_verified).length),
            label: t("stats.verified"),
            tint: "green",
          },
          {
            icon: "cottage",
            value: String(members.filter((m) => m.is_host).length),
            label: t("stats.hosts"),
            tint: "amber",
          },
          {
            icon: "campaign",
            value: String(members.filter((m) => m.is_promoter).length),
            label: t("stats.promoters"),
            tint: "info",
          },
        ]}
      />

      <div className="rec-card">
        <DataTable
          columns={columns}
          rows={rows}
          onRow={(row) => openMember(row.id)}
          search={t("table.search", { defaultValue: "Procurar membro ou email…" })}
          searchKeys={["name", "email"]}
          filters={
            <Segmented options={statusOptions} value={statusFilter} onChange={setStatusFilter} />
          }
        />
      </div>
    </div>
  );
}

export default ListMembers;
